import { SupText } from "@repo/core-ui";
import React, { type ElementType, type JSX, forwardRef, createElement, ForwardedRef } from "react";
import { twMerge } from "tailwind-merge";

export interface TabProps<P extends object = object> {
  as?: ElementType;
  label: string;
  value?: string | number;
  prefixIcon?: JSX.Element;
  suffixIcon?: JSX.Element;
  active?: boolean;
  disabled?: boolean;
  className?: string;
  orientation?: "horizontal" | "vertical";
  indicatorPosition?: "top" | "bottom" | "left" | "right";
  onClick?: () => void;
  props?: P;
}

const TAB_STYLES = {
  horizontal: "items-center justify-center px-4 h-10 flex-1",
  vertical: "flex-col items-center justify-center px-1 py-2 flex-1",
};

export const SupTab = forwardRef(function SupTab<P extends object>(
  { as = "button", label, prefixIcon, suffixIcon, orientation = "horizontal", active = false, disabled = false, className, onClick, value, props }: TabProps<P>,
  ref: ForwardedRef<HTMLElement>
) {
  const children = (
    <>
      {prefixIcon && <span className="inline-flex h-10 w-10 items-center justify-center group-disabled/tab-item:text-neutral-500">{prefixIcon}</span>}
      <SupText as="span" variant="button" weight={active ? "regular" : "regular"} className="text-center">
        {label}
      </SupText>
      {suffixIcon && <span className="inline-flex h-10 w-10 items-center justify-center group-disabled/tab-item:text-neutral-500">{suffixIcon}</span>}
    </>
  );

  return createElement(
    as,
    {
      ref,
      disabled,
      className: twMerge(
        "sup-tab-item group/tab-item inline-flex mx-1 shrink-0 text-neutral-950 bg-neutral-200 transition-colors duration-200 flex-1 ",
        disabled && "sup-tab-item-disabled",
        !disabled
          ? "hover:text-neutral-50 hover:bg-primary-900 focus:text-neutral-50 focus:bg-primary-900 active:text-neutral-950 active:bg-primary-900 [&.sup-tab-item-active]:text-neutral-50 [&.sup-tab-item-active]:bg-primary-900"
          : "[&.sup-tab-item-disabled]:text-neutral-300 [&.sup-tab-item-disabled]:bg-neutral-50 disabled:text-neutral-400 disabled:bg-neutral-50",
        TAB_STYLES[orientation],
        active && !disabled && active && !disabled && "sup-tab-item-active",
        className
      ),
      onClick,
      "data-value": value,
      ...props,
    },
    children
  );
});
